/**
 * First-party engagement tracking for the citable market pages (weekly market
 * report and the TCG price index).
 *
 * Google Analytics cannot tell us which index constituent a reader followed or
 * whether the citation line was copied, and both are how these pages earn
 * links, so the admin console reads these first-party events instead.
 */
import { useEffect, useRef } from "react";
import { logReportEvent } from "@/lib/report-analytics.functions";

export type ReportEventKind = "report_view" | "card_click" | "citation_copy" | "feed_open" | "index_view" | "snapshot_open";

function referrerHost(): string | undefined {
  try {
    return document.referrer ? document.referrer.split("/")[2]?.slice(0, 120) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fire-and-forget event log, callable from any component without prop drilling
 * (index card clicks happen deep inside several panels).
 */
export function trackReportEvent(kind: ReportEventKind, target?: string) {
  if (typeof window === "undefined") return;
  void logReportEvent({
    data: {
      kind,
      target: target?.slice(0, 60),
      referrerHost: referrerHost(),
      device: window.matchMedia("(max-width: 640px)").matches ? "mobile" : "desktop",
    },
  }).catch(() => {});
}

/** Returns a `track(kind, target?)` function and logs `viewKind` once on mount. */
export function useReportTracking(viewKind: ReportEventKind = "report_view") {
  const sent = useRef(false);

  const track = (kind: ReportEventKind, target?: string) => trackReportEvent(kind, target);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    track(viewKind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return track;
}
